import React from "react";
import { useTranslation } from "react-i18next";

import Heading from "@components/layout/Heading";
import Label from "@components/form/Label";
import DecimalInput from "@components/form/DecimalInput";
import TextInput from "@components/form/TextInput";
import TextArea from "@components/form/TextArea";
import RadioInput from "@components/form/RadioInput";
import { useAdjustDeclarationContext } from "@context/AdjustDeclarationContext";
import { formatVolume } from "@utils/formatVolume";
import { convertMLToLiter } from "@utils/convertUnits";
import { formatDateInput } from "@utils/formatDate";
import { extractionRightTypes } from "@services/extractionRight";

type EnterAdjustDeclarationDetailsProps = {
  onCancel: () => void;
  onNext: () => void;
};

const EnterAdjustDeclarationDetails: React.FunctionComponent<
  EnterAdjustDeclarationDetailsProps
> = ({ onNext, onCancel }) => {
  const { t } = useTranslation();
  const { details, setDetails } = useAdjustDeclarationContext();

  const currentUsageInLiter = {
    waterHarvesting: 0,
    specialAnnouncement: 0,
    allocationUsage: 0,
    overUsage: 0,
  };

  // Parse usage data from rightTypeProportions
  for (const item of details.rightTypeProportions) {
    for (const outerKey in item) {
      const innerObject = item[outerKey];
      for (const key in innerObject) {
        const intKey = parseInt(key);
        const value = innerObject[key];
        if (intKey === extractionRightTypes.WH) {
          currentUsageInLiter.waterHarvesting += value;
        } else if (intKey === extractionRightTypes.WA) {
          currentUsageInLiter.allocationUsage += value;
        } else if (intKey === extractionRightTypes.SA) {
          currentUsageInLiter.specialAnnouncement += value;
        } else {
          currentUsageInLiter.overUsage += value;
        }
      }
    }
  }

  // Convert new usage (ML) to liters, handle "" case
  const newUsageInLiter = convertMLToLiter(Number(details.newUsage || 0));

  // Total current usage
  const currentTotalInLiter =
    currentUsageInLiter.allocationUsage +
    currentUsageInLiter.waterHarvesting +
    currentUsageInLiter.specialAnnouncement +
    currentUsageInLiter.overUsage;

  // Compute adjustment
  const allUsageAdjustmentInLiter = newUsageInLiter - currentTotalInLiter;
  const isPositive = allUsageAdjustmentInLiter > 0;

  let allocationAdjustment = 0;
  let harvestingAdjustment = 0;
  let specialAnnouncementAdjustment = 0;

  if (isPositive) {
    // All positive adjustment goes to allocation usage
    allocationAdjustment = allUsageAdjustmentInLiter;
  } else {
    const reduction = Math.abs(allUsageAdjustmentInLiter);

    // Step 1: Reduce from water harvesting
    const specialAnnouncementReduction = Math.min(
      reduction,
      currentUsageInLiter.specialAnnouncement,
    );
    let remainingReduction = reduction - specialAnnouncementReduction;

    // Step 2: Reduce from water harvesting
    const harvestReduction = Math.min(
      remainingReduction,
      currentUsageInLiter.waterHarvesting,
    );
    remainingReduction -= harvestReduction;

    // Step 3: Reduce from allocation usage
    const allocReduction = Math.min(
      remainingReduction,
      currentUsageInLiter.allocationUsage,
    );
    remainingReduction -= allocReduction;

    // Step 4: Reduce from over usage
    const overUsageReduction = Math.min(
      remainingReduction,
      currentUsageInLiter.overUsage,
    );

    // Now set adjustments
    allocationAdjustment = -(allocReduction + overUsageReduction); // allocation bucket includes overUsage
    harvestingAdjustment = -harvestReduction;
    specialAnnouncementAdjustment = -specialAnnouncementReduction;
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onNext();
  };

  return (
    <form className="p-6 flex flex-col grow gap-6" onSubmit={handleSubmit}>
      <Heading light>{t("balance_adjustment.meter_reading.reset")}</Heading>
      <div className="max-w-md">
        <Label>{t("meter.serial_no")}</Label>
        <TextInput value={details.serialNo} readOnly disabled />
      </div>
      <fieldset>
        <div className="flex gap-6 mt-4">
          <RadioInput
            name="type"
            id="meter"
            label={t("balance_adjustment.meter_reading.reading")}
            checked={details.type === "reading"}
            onChange={e => {
              if (e.target.checked) {
                setDetails(prev => ({ ...prev, type: "reading" }));
              }
            }}
            inline
          />
          <RadioInput
            name="type"
            id="usage"
            label={t("declaration.volume")}
            checked={details.type === "usage"}
            onChange={e => {
              if (e.target.checked) {
                setDetails(prev => ({ ...prev, type: "usage" }));
              }
            }}
            inline
          />
        </div>
      </fieldset>
      <fieldset className="flex gap-6">
        <div className="flex">
          {details.type === "reading" && details.currentReading ? (
            <div className="flex gap-6">
              <div>
                <Label>{t("declaration.form.last_read")}</Label>
                <p className="font-bold text-3xl">{details.currentReading}</p>
              </div>
              <div>
                <Label htmlFor="reading">
                  {t("balance_adjustment.meter_reading.new_reading")}
                </Label>
                <DecimalInput
                  id="reading"
                  value={details.newReading}
                  onChange={val => {
                    setDetails(prev => ({
                      ...prev,
                      newReading: val,
                    }));
                  }}
                />
              </div>
            </div>
          ) : null}

          {details.type === "usage" && (
            <div className="flex gap-6">
              <div>
                <Label>
                  {t("balance_adjustment.meter_reading.current_usage")}
                </Label>
                <p className="font-bold text-3xl whitespace-nowrap">
                  {details.currentUsage} {t("common.volume_unit")}
                </p>
              </div>
              <div>
                <Label htmlFor="usage">
                  {t("balance_adjustment.meter_reading.new_usage")}
                </Label>
                <TextInput
                  type="number"
                  id="usage"
                  step={0.001}
                  min={0}
                  value={details.newUsage}
                  suffix={t("common.volume_unit")}
                  onChange={e => {
                    setDetails(prev => ({
                      ...prev,
                      newUsage: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="w-1/3">
                <Label>{t("declaration.allocation_usage")}</Label>
                <p className="mt-1 font-bold text-3xl whitespace-nowrap">
                  {allocationAdjustment === 0 || details.newUsage === ""
                    ? ""
                    : allocationAdjustment > 0
                      ? "+"
                      : "-"}
                  {details.newUsage === ""
                    ? 0
                    : formatVolume(Math.abs(allocationAdjustment))}
                </p>
              </div>

              {!isPositive &&
              details.newUsage !== "" &&
              harvestingAdjustment < 0 ? (
                <div className="w-1/3">
                  <Label>{t("declaration.harvesting_usage")}</Label>
                  <p className="mt-1 font-bold text-3xl whitespace-nowrap">
                    {harvestingAdjustment === 0
                      ? ""
                      : harvestingAdjustment > 0
                        ? "+"
                        : "-"}
                    {formatVolume(Math.abs(harvestingAdjustment))}
                  </p>
                </div>
              ) : null}

              {!isPositive &&
              details.newUsage !== "" &&
              specialAnnouncementAdjustment < 0 ? (
                <div className="w-1/3">
                  <Label>{t("declaration.special_usage")}</Label>
                  <p className="mt-1 font-bold text-3xl whitespace-nowrap">
                    {specialAnnouncementAdjustment === 0
                      ? ""
                      : specialAnnouncementAdjustment > 0
                        ? "+"
                        : "-"}
                    {formatVolume(Math.abs(specialAnnouncementAdjustment))}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </fieldset>
      <div className="max-w-xl">
        <Label htmlFor="effectiveAt">{t("common.effective_date")}</Label>
        <TextInput
          type="date"
          id="effectiveAt"
          value={formatDateInput(details.effectiveAt)}
          min={details.readAt ? formatDateInput(details.readAt) : ""}
          onChange={(e: any) =>
            setDetails(prev => ({
              ...prev,
              effectiveAt: new Date(e.target.value),
            }))
          }
        />
      </div>
      <div className="max-w-xl">
        <Label htmlFor="note" optional>
          {t("common.note")}
        </Label>
        <TextArea
          id="note"
          value={details.note}
          onChange={(e: any) =>
            setDetails(prev => ({
              ...prev,
              note: e.target.value,
            }))
          }
          rows={8}
        />
      </div>
      <div className="grow" />
      <footer className="flex gap-4 -mx-6 p-6 pb-0 border-t border-gray-200">
        <button
          type="submit"
          className="btn-primary"
          disabled={
            (details.type === "reading" && details.newReading === "") ||
            (details.type === "usage" && details.newUsage === "") ||
            (details.currentReading === details.newReading &&
              Number(details.currentUsage) === Number(details.newUsage))
          }
        >
          {t("common.next_step")}
        </button>
        <button
          type="button"
          className="btn-outline-primary"
          onClick={onCancel}
        >
          {t("common.cancel")}
        </button>
      </footer>
    </form>
  );
};

export default EnterAdjustDeclarationDetails;
